import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import LoadingOverlay from "../common/LoadingOverlay/LoadingOverlay";
import './ProjectsScreen.css'
import { faFolder, faEye, faFolderOpen, faPlus, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { getAuthToken } from "../../utils/authStorage";
import { useUser } from "../../context/UserContext";

const ProjectScreen = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [inspectionFilters, setInspectionFilters] = useState({});
  const [domainFilter, setDomainFilter] = useState('ALL');
  const [modalityFilter, setModalityFilter] = useState('ALL');
  const navigate = useNavigate();
  const { isViewer } = useUser();

  const fetchProjects = async () => {
    try {
      const token = getAuthToken();

      const res = await axios.get('/api/projects', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setProjects(data);

      const inspectionsByProject = await Promise.all(
        data.map(async (project) => {
          try {
            const response = await axios.get(
              `/api/inspections/by-project/${project.id}/cards`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            return [project.id, Array.isArray(response.data) ? response.data : []];
          } catch (error) {
            console.error(`Error loading inspections for project ${project.id}:`, error);
            return [project.id, []];
          }
        }),
      );
      setInspectionFilters(Object.fromEntries(inspectionsByProject));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const domains = useMemo(() => Array.from(new Set(
    Object.values(inspectionFilters).flat().map((inspection) => inspection.inspectionDomain).filter(Boolean),
  )).sort(), [inspectionFilters]);

  const modalities = useMemo(() => Array.from(new Set(
    Object.values(inspectionFilters).flat().map((inspection) => inspection.imageModality).filter(Boolean),
  )).sort(), [inspectionFilters]);

  const domainLabels = {
    ELECTRIC_NETWORK: 'Electric networks',
  };

  const modalityLabels = {
    RGB: 'RGB images',
    THERMAL: 'Thermal images',
  };

  const ordered = useMemo(() => projects
    .filter((project) => {
      const inspections = inspectionFilters[project.id] || [];
      return (
        (domainFilter === 'ALL' || inspections.some((inspection) => inspection.inspectionDomain === domainFilter))
        && (modalityFilter === 'ALL' || inspections.some((inspection) => inspection.imageModality === modalityFilter))
      );
    })
    .sort((a, b) => {
      const rawDateA = new Date(a.creationDate || a.createdAt || a.created_at || 0).getTime();
      const rawDateB = new Date(b.creationDate || b.createdAt || b.created_at || 0).getTime();
      const dateA = Number.isNaN(rawDateA) ? 0 : rawDateA;
      const dateB = Number.isNaN(rawDateB) ? 0 : rawDateB;
      return dateB - dateA || (a.name || '').localeCompare(b.name || '');
    }), [projects, inspectionFilters, domainFilter, modalityFilter]);

  const onClickNewProject = () => {
    console.log("Crear nuevo proyecto")
    navigate("/projects/new", {});
  }
  const formatDate = (d) => {
    if (!d) return 'No date';
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d; // por si ya viene formateado
      return dt.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return d; }
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    setDeleteError('');
    try {
      const token = getAuthToken();
      await axios.delete(`/api/projects/${projectToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects((current) => current.filter((project) => project.id !== projectToDelete.id));
      setProjectToDelete(null);
    } catch (err) {
      console.error("Error deleting project:", err);
      setDeleteError(
        err.response?.data?.message
        || err.response?.data?.error
        || "Could not delete the project.",
      );
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div>
      <LoadingOverlay show={loading} text="Loading projects…" />
      <div className="project-list-container">
        <div className="project-list-header">
          <h1 className="title">Projects</h1>
          {!isViewer && (
            <button className="new-project-button" onClick={onClickNewProject}>
              + New Project
            </button>
          )}
        </div>

        <div className="project-filters" aria-label="Project filters">
          <label>
            Inspection type
            <select value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)}>
              <option value="ALL">All types</option>
              {domains.map((domain) => (
                <option key={domain} value={domain}>{domainLabels[domain] || domain}</option>
              ))}
            </select>
          </label>
          <label>
            Image modality
            <select value={modalityFilter} onChange={(event) => setModalityFilter(event.target.value)}>
              <option value="ALL">All modalities</option>
              {modalities.map((modality) => (
                <option key={modality} value={modality}>{modalityLabels[modality] || modality}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid">
          {!loading && projects.length === 0 && (
            <div className="projects-empty">
              <div className="projects-empty-icon">
                <FontAwesomeIcon icon={faFolderOpen} />
              </div>
              <h3 className="projects-empty-title">No projects yet</h3>
              <p className="projects-empty-sub">
                {isViewer
                  ? "No analyzed projects are available for this viewer profile yet."
                  : "Create your first project to start organizing your inspections."}
              </p>
              {!isViewer && (
                <button className="projects-empty-btn" onClick={onClickNewProject}>
                  <FontAwesomeIcon icon={faPlus} /> New Project
                </button>
              )}
            </div>
          )}
          {!loading && projects.length > 0 && ordered.length === 0 && (
            <div className="projects-empty">
              <div className="projects-empty-icon">
                <FontAwesomeIcon icon={faFolderOpen} />
              </div>
              <h3 className="projects-empty-title">No matching projects</h3>
              <p className="projects-empty-sub">Try changing the inspection filters.</p>
            </div>
          )}
          {ordered.map((project) => (
            <article key={project.id} className="project-card">
              <div className="project-header">
                <div className="doc-icon" aria-hidden>
                  <FontAwesomeIcon icon={faFolder} />
                </div>
                <div className="title-wrap">
                  <h2 className="project-name">{project.name || 'Untitled Project'}</h2>
                  {project.status && (
                    <span className={`status-badge ${
                      project.status?.toLowerCase() === 'completed' ? 'is-completed' : 'is-active'
                    }`}>
                      {project.status?.toString().toUpperCase()}
                    </span>
                  )}
                </div>
                {!isViewer && (
                  <button
                    type="button"
                    className="project-delete-button"
                    title="Delete project"
                    aria-label={`Delete ${project.name || 'project'}`}
                    onClick={() => {
                      setDeleteError('');
                      setProjectToDelete(project);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>

              <p className="description">
                {project.description || 'No Description'}
              </p>

              <p className="dateText">
                <span className="muted">Created</span>&nbsp;{formatDate(project.creationDate)}
              </p>

              <button
                className="details-button"
                onClick={() =>
                  navigate(`/projects/${project.id}`, {
                    state: {
                      projectName: project.name,
                      projectDescription: project.description,
                      creationDate: project.creationDate,
                    },
                  })
                }
              >
                 <FontAwesomeIcon icon={faEye} /> View Details
              </button>
            </article>
          ))}
        </div>

        {projectToDelete && (
          <div className="project-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-project-title">
            <div className="project-confirm-modal">
              <header className="project-confirm-header">
                <h2 id="delete-project-title">Delete Project</h2>
                <button
                  type="button"
                  className="project-confirm-close"
                  onClick={() => !isDeleting && setProjectToDelete(null)}
                  aria-label="Close"
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>
              <p className="project-confirm-text">
                Are you sure you want to delete “{projectToDelete.name}”?
              </p>
              <p className="project-confirm-warning">
                All inspections, images, analyses, reports and notifications associated with this project will be permanently deleted.
              </p>
              {deleteError && <p className="project-confirm-error">{deleteError}</p>}
              <footer className="project-confirm-actions">
                <button
                  type="button"
                  className="project-confirm-cancel"
                  onClick={() => setProjectToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="project-confirm-delete"
                  onClick={confirmDeleteProject}
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {isDeleting ? "Deleting..." : "Delete Project"}
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectScreen;
